import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isProviderSalePending } from '../../../api/hooks/viva/useSaleState';
import { InvoiceInterface } from '../../../interfaces/invoice.interface';
import { PATHS } from '../../../navigation/data';
import { splitToPagesDynamic } from '../../../services/utils/pdf';
import { EmailPill } from '../../atoms/EmailPill';
import { LinkTo } from '../../atoms/LinkTo';
import { DocViewPage } from './DocViewPage';
import './docViewStyles.scss';

interface Props {
  doc: InvoiceInterface;
  logo: string | null | undefined;
  isPublic?: boolean;
}

export const DocView = ({ doc, logo, isPublic = false }: Props) => {
  const { t } = useTranslation();

  const getPages = useCallback(() => {
    const splitProds = splitToPagesDynamic(doc?.products);

    return splitProds.map((prods, index) => (
      <DocViewPage
        key={index}
        company={doc?.company}
        doc={doc}
        products={prods}
        index={index}
        numberOfPages={splitProds.length}
        logo={logo}
      />
    ));
  }, [doc, logo]);

  const getEmailPill = useCallback(() => {
    if (doc?.emailSentToCustomerAt && !doc?.failedAt) {
      return <EmailPill size={16} top={-10} date={doc?.emailSentToCustomerAt} />;
    }
  }, [doc?.emailSentToCustomerAt, doc?.failedAt]);

  const getProviderPostingInProgress = useCallback(() => {
    if (isPublic) return;

    if (isProviderSalePending(doc.id, doc)) {
      return (
        <div className="text-center mediumText5" style={{ color: 'var(--ion-color-secondary)' }}>
          {t('SALE.POST_TO_PROVIDER_IN_PROGRESS_1')}
          <br />
          {t('SALE.POST_TO_PROVIDER_IN_PROGRESS_2')}
          <br />
          {t('SALE.POST_TO_PROVIDER_IN_PROGRESS_3')}
        </div>
      );
    }
  }, [doc, isPublic, t]);

  const getProviderFailure = useCallback(() => {
    if (isPublic) return;

    if (doc?.post_with_provider && doc?.sentToAade && doc?.failedAt) {
      return (
        <LinkTo link={`${PATHS.CLONE_SALE}/${doc?.id}`} data={{ id: doc?.id }}>
          <div className="text-center mediumText5" style={{ color: 'var(--ion-color-warning)' }}>
            {t('SALE.UNABLE_TO_POST_TO_PROVIDER_1')}
            <br />
            {t('SALE.UNABLE_TO_POST_TO_PROVIDER_2')}
          </div>
        </LinkTo>
      );
    }
  }, [doc?.failedAt, doc?.id, doc?.post_with_provider, doc?.sentToAade, isPublic, t]);

  return (
    <div className="margin-b-80 padding-t-10">
      {getEmailPill()}
      {getProviderPostingInProgress()}
      {getProviderFailure()}
      {getPages()}
    </div>
  );
};
